import BaseComponent from 'scripts/BaseComponent.js';
import UnderlayHelper from '../../global/underlay-helper';
import { AccessibilityHelper } from '../../../scripts/accessibility/accessibilityHelper';

export default class ProductGrid extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      filterToggleBtn: '.js-filterToggleBtn',
      desktopFilterBtn: '.js-desktopFilterBtn',
      filterDrawer: '.js-filterDrawer',
      productGrid: '.js-productGrid',
      categoryToggleBtn: '.js-categoryToggleBtn',
      categoriesDrawer: '.js-categoriesDrawer',
      productGridHeaderObserver: '.js-productGridHeaderObserver',
      productGridHeader: '.js-productGridHeader',
      productGridHeaderSpacer: '.js-productGridHeaderSpacer',
      navigation: '.js-navigation',
    };

    this.dom = {
      $component: component,
      $filterToggleBtn: this.$getAll(this.refs.filterToggleBtn),
      $desktopFilterBtn: this.$get(this.refs.desktopFilterBtn),
      $filterDrawer: this.$get(this.refs.filterDrawer),
      $productGrid: this.$get(this.refs.productGrid),
      $categoryToggleBtn: this.$getAll(this.refs.categoryToggleBtn),
      $categoriesDrawer: this.$get(this.refs.categoriesDrawer),
      $productGridHeaderObserver: this.$get(this.refs.productGridHeaderObserver),
      $productGridHeader: this.$get(this.refs.productGridHeader),
      $productGridHeaderSpacer: this.$get(this.refs.productGridHeaderSpacer),
      $navigation: document.querySelector(this.refs.navigation),
    };

    this.state = {
      filterOpen: false,
      filterOpenClass: 'open-filter',
      filterUnderlay: new UnderlayHelper({ overNav: true }),
      desktopBreakpoint: '(min-width: 1024px)',
      categoriesOpen: false,
      categoriesOpenClass: 'open-categories',
      categoriesUnderlay: new UnderlayHelper({ overNav: true }),
      accessibilityHelper: new AccessibilityHelper(),
      focusTrap: null,
      fixedHeaderClass: 'fixed-header',
    };

    this.mount();
  }

  mount() {
    const { dom, state } = this;
    super.mount();

    this.observe();

    this.setClick(dom.$filterToggleBtn, this.toggleFilter);
    state.filterUnderlay.addClickListener(this.closeFilter.bind(this));

    if (dom.$categoriesDrawer) {
      this.setClick(dom.$categoryToggleBtn, this.toggleCategories);
      state.categoriesUnderlay.addClickListener(this.closeCategories.bind(this));
    }
  }

  observe() {
    const { dom } = this;
    const offset = dom.$navigation.offsetHeight;

    const observer = new IntersectionObserver(this.onIntersection.bind(this), {
      rootMargin: `-${offset}px 0px 0px 0px`,
    });
    observer.observe(dom.$productGridHeaderObserver);
  }

  onIntersection([entry]) {
    const { dom, state } = this;
    if (window.matchMedia(state.desktopBreakpoint).matches) return;

    if (entry.isIntersecting) {
      dom.$productGridHeader.classList.remove(state.fixedHeaderClass);
      dom.$productGridHeaderSpacer.classList.remove(state.fixedHeaderClass);
    } else {
      dom.$productGridHeader.classList.add(state.fixedHeaderClass);
      dom.$productGridHeaderSpacer.classList.add(state.fixedHeaderClass);
    }
  }

  toggleFilter() {
    const { state } = this;

    if (state.filterOpen) {
      this.closeFilter();
    } else {
      this.openFilter();
    }
  }

  openFilter() {
    const { dom, state } = this;

    dom.$filterDrawer.classList.add(state.filterOpenClass);
    dom.$productGrid.classList.add(state.filterOpenClass);
    dom.$desktopFilterBtn.classList.add(state.filterOpenClass);
    dom.$filterDrawer.setAttribute('aria-hidden', 'false');
    dom.$filterDrawer.setAttribute('aria-expanded', 'true');

    if (!window.matchMedia(state.desktopBreakpoint).matches) {
      state.filterUnderlay.open();
    }

    state.focusTrap = state.accessibilityHelper.buildFocusTrap([dom.$filterDrawer]);
    state.focusTrap.set();
    state.filterOpen = true;
  }

  closeFilter() {
    const { dom, state } = this;

    dom.$filterDrawer.classList.remove(state.filterOpenClass);
    dom.$productGrid.classList.remove(state.filterOpenClass);
    dom.$desktopFilterBtn.classList.remove(state.filterOpenClass);
    dom.$filterDrawer.setAttribute('aria-hidden', 'true');
    dom.$filterDrawer.setAttribute('aria-expanded', 'false');
    state.filterUnderlay.close();

    if (state.focusTrap) {
      state.focusTrap.unset();
    }

    state.focusTrap = null;
    state.filterOpen = false;
  }

  toggleCategories() {
    const { state } = this;

    if (state.categoriesOpen) {
      this.closeCategories();
    } else {
      this.openCategories();
    }
  }

  openCategories() {
    const { dom, state } = this;

    dom.$categoriesDrawer.classList.add(state.categoriesOpenClass);
    dom.$categoriesDrawer.setAttribute('aria-hidden', 'false');
    dom.$categoriesDrawer.setAttribute('aria-expanded', 'true');
    state.categoriesUnderlay.open();
    state.focusTrap = state.accessibilityHelper.buildFocusTrap([dom.$categoriesDrawer]);
    state.focusTrap.set();
    state.categoriesOpen = true;
  }

  closeCategories() {
    const { dom, state } = this;

    dom.$categoriesDrawer.classList.remove(state.categoriesOpenClass);
    dom.$categoriesDrawer.setAttribute('aria-hidden', 'true');
    dom.$categoriesDrawer.setAttribute('aria-expanded', 'false');
    state.categoriesUnderlay.close();

    if (state.focusTrap) {
      state.focusTrap.unset();
    }

    state.focusTrap = null;
    state.categoriesOpen = false;
  }
}
